import { api } from './api';

export interface PendingRequest {
  id: number;
  student_name: string;
  classroom_name: string;
  status: 'pending' | 'accepted' | 'rejected' | string;
}

export interface BackendClassSummary {
  id: number;
  name: string;
}

export interface BackendClassroomStats {
  rating_percent: number | null;
  rating_percent_prev?: number | null;
  rating_delta?: number | null;
  rating_trend?: 'up' | 'down' | 'flat' | null;
  rating_trend_window_days?: number | null;
  top_rank?: number | null;
  top_message?: string | null;
  school?: {
    id?: number | null;
    name?: string | null;
    rating_percent?: number | null;
    rating_percent_prev?: number | null;
    rating_delta?: number | null;
    rating_trend?: 'up' | 'down' | 'flat' | null;
    rating_trend_window_days?: number | null;
  } | null;
}

export interface BackendLesson {
  start: string;
  end: string;
  zi: string;
  subjectId: number | null;
  materie: string;
  comentariu: string | null;
  profesor: string | null;
  tip: string | null;
  adaugataDeProfesorCurent: boolean;
}

export interface BackendTodayLesson {
  id: number;
  classroom_id: number;
  classroom_name: string;
  timetable_id: number;
  timetable_name: string;
  day_of_week: number;
  start_time: string;
  end_time: string;
  subject: string;
  room?: string | null;
  lesson_type?: string | null;
  comment?: string | null;
}

export interface NotificationItem {
  id: number;
  title: string;
  content: string;
  link?: string | null;
  is_read: boolean;
  created_at: string;
}

export type PagedResponse<T> = { items: T[]; total: number; page: number; page_size: number };

export async function fetchMyClasses(): Promise<BackendClassSummary[]> {
  const resp = await api.get('/api/users/my-classes');
  return resp.data;
}

export async function fetchClassroomStats(classId: number): Promise<BackendClassroomStats> {
  const resp = await api.get(`/api/scoli/classrooms/${classId}/stats`);
  return resp.data;
}

export async function fetchStudentsInClass(classId: number): Promise<Array<{ id: number; full_name: string; email: string }>> {
  const resp = await api.get(`/api/scoli/clasa/${classId}/elevi`);
  return resp.data;
}

export async function fetchMySchedule(): Promise<BackendLesson[]> {
  const resp = await api.get('/api/orar/my-orar');
  return resp.data;
}

export async function fetchMyTodaySchedule(dayOfWeek: number): Promise<BackendTodayLesson[]> {
  const resp = await api.get('/api/orar/my-today', { params: { day_of_week: dayOfWeek } });
  return resp.data;
}

export async function fetchPendingRequests(): Promise<PendingRequest[]> {
  const resp = await api.get('/api/users/pending-requests');
  return resp.data;
}

export async function approveRequest(requestId: number): Promise<void> {
  await api.post('/api/users/handle-request', { request_id: requestId, action: 'approve' });
}

export async function rejectRequest(requestId: number): Promise<void> {
  await api.post('/api/users/handle-request', { request_id: requestId, action: 'reject' });
}

export async function fetchNotificationsPaged(params?: {
  unread_only?: boolean;
  page?: number;
  page_size?: number;
}): Promise<PagedResponse<NotificationItem>> {
  const resp = await api.get('/api/notificari/notifications', { params: params || undefined });
  return resp.data as any;
}

// Back-compat helper for old UIs that only need a few items.
export async function fetchNotifications(): Promise<NotificationItem[]> {
  const data = await fetchNotificationsPaged({ page: 1, page_size: 20 });
  return Array.isArray(data?.items) ? data.items : [];
}

export async function fetchUnreadNotificationsCount(): Promise<number> {
  const resp = await api.get('/api/notificari/notifications/unread-count');
  const unread = Number((resp.data as any)?.unread ?? 0);
  return Number.isFinite(unread) ? unread : 0;
}

export async function markAllNotificationsRead(): Promise<void> {
  await api.post('/api/notificari/notifications/mark-as-read');
}

export async function markNotificationRead(notificationId: number): Promise<void> {
  await api.post(`/api/notificari/notifications/${notificationId}/read`);
}

export async function deleteNotifications(ids: number[]): Promise<number> {
  const resp = await api.post('/api/notificari/notifications/delete', { ids });
  const deleted = Number((resp.data as any)?.deleted ?? 0);
  return Number.isFinite(deleted) ? deleted : 0;
}
